"use client"

import Image, { ImageProps } from "next/image"
import { useState } from "react"
import { clx } from "@medusajs/ui"

type BlurImageProps = ImageProps & {
  shimmerClassName?: string
}

/**
 * BlurImage — next/image wrapper that shows a shimmer skeleton while loading.
 * Works with the custom wsrv.nl loader (no blurDataURL needed).
 *
 * FIX (SHOP-140): Shimmer span is always present in DOM but opacity-controlled.
 * Previous implementation used conditional rendering ({!loaded && <span>}) which
 * caused React removeChild DOM errors during hydration — the DOM diverged from
 * the VDOM when image.onLoad fired before React could reconcile the shimmer removal.
 * CSS visibility avoids any DOM mutation mismatch.
 */
const BlurImage = ({ className, shimmerClassName, onLoad, ...props }: BlurImageProps) => {
  const [loaded, setLoaded] = useState(false)

  return (
    <span className="relative block w-full h-full">
      {/* Shimmer skeleton overlay — always in DOM, hidden via opacity after load */}
      <span
        aria-hidden
        className={clx(
          "absolute inset-0 z-10 shimmer-placeholder transition-opacity duration-300 pointer-events-none",
          loaded ? "opacity-0" : "opacity-100",
          shimmerClassName
        )}
      />
      <Image
        {...props}
        className={clx(
          className,
          "transition-opacity duration-500",
          loaded ? "opacity-100" : "opacity-0"
        )}
        onLoad={(e) => {
          setLoaded(true)
          onLoad?.(e)
        }}
      />
    </span>
  )
}

export default BlurImage
